import { createModule } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import * as Track from '@project/shared/utils/tracking/js/tracking';
import { formatLabel } from '@project/shared/utils/labels/js/labels';

// @ts-ignore
import * as IScroll from 'iscroll';
import getCurrentBreakpoint, { onBreakpointChange } from '@project/shared/utils/breakpoints/js/breakpoints';

const classes = {
	scroller: 'js-m-slider-gallery__scroller',
	list: 'js-m-slider-gallery__list',
	item: 'js-m-slider-gallery__item',
	dotButton: 'js-m-slider-gallery__dot-button',
	dot: 'js-m-slider-gallery__dot',
	activeDot: 'state-m-slider-gallery--dot-is-active',
	sliderPages: 'js-m-slider-gallery__pages',
	sliderNavigation: 'js-m-slider-gallery__navigation',
	showNumber: 'state-m-slider-gallery__show-number',
	showArrow: 'state-m-slider-gallery--arrow-is-shown',
	arrowLeft: 'js-m-slider-gallery__arrow--left',
	arrowRight: 'js-m-slider-gallery__arrow--right',
	metaNaviFlyout: 'js-m-navigation-meta__flyout',
	jsCaseStudyGallery: 'js-slider-gallery--case-studies',
	link: 'js-m-slider-gallery__item-link',
	isInitialized: 'state-m-slider-gallery--is-initialized',
	videosContainer: 'js-m-slider-gallery__list',
	videosContainerListItem: 'js-m-slider-gallery__item',
	videoElement: 'js-a-video__element',
	campaignTeaserArea: 'js-m-slider-gallery__campaign-tease',
};

/**
 * Recommended settings for typeOnStopWhenSkippingSlide: "pause" or "stop" when skipping slides
 * @type {string}
 */
const videoCfg = {
	typeOnStopWhenSkippingSlide: 'pause',
	targetId: 'video-id',
};

@createModule('SliderGallery')
/**
 * Breadcrumb module implementation.
 *
 * @author Christoph Merkelbach <christoph.merkelbach@namics.com>
 */
export class SliderGallery extends TerrificSpec {
	sliderGalleryScroller: IScroll;
	$ctx: JQuery;
	$dots: JQuery;
	$sliderPages: JQuery;
	$sliderNavigation: JQuery;
	$campaignTeaserArea: JQuery;
	$arrowLeft: JQuery;
	$arrowRight: JQuery;
	$scroller: JQuery;
	$items: JQuery;
	$list: JQuery;
	$imagesAndVideos: JQuery;
	$dotContainers: JQuery;
	$links: JQuery;
	itemMargin: number;
	numberOfItemsPerPageBeforeViewportChanged: number;
	activeItemBeforeViewportChanged: number;
	sliderIsScrolling: boolean;
	itemsPerPage: Array<number>;
	isMobileSlider: boolean;
	_sliderPageIndex: number;
	_showDotsSlider: number;

	start(resolve: Function) {
		this.initVars();

		// make sure margin of all bullets except the last one is set to known value in the javascript
		this.$items.not(':last-child').css('margin-right', `${this.itemMargin}px`);

		if (this.$items.length > this._showDotsSlider) {
			this.$sliderNavigation.addClass(classes.showNumber);
		}

		this.addDataItemAttribute();

		this.initScroller();
		onBreakpointChange(() => {
			this.reinitScrollerWhenViewportChangedToOrFromSmall();
		});

		// do the positioning and sizing again, when window is resized
		$(window).on('resize.SliderGalleryAdjustPageWidth', () => {
			this.handleSizeAndPosition();
		});

		if (this.$items.first().hasClass('m-slider-gallery__video-item')) {
			var me = this;
			var interval = setInterval(function () {
				me.handleSizeAndPosition();
				if (me.$list.height() > 140) {
					clearInterval(interval)
				}
			}, 1000);
		}

		$(document).ready(function () {
			$(".m-slider-gallery__tease-headline__descriptiontext").each(function (i) {
				var len = $(this).text().trim().length;
				if (len > 60) {
					$(this).text($(this).text().substr(0, 60));
				}
			});
		});

		this.handleVideoImageLoading();
		this.handleSizeAndPosition();
		this.handleCampaignTeaserClick();
		this.initNavigationArrows();
		this.setActiveDot(0);

		resolve();
	}

	addDataItemAttribute() {
		this.$ctx.find('li.js-m-slider-gallery__item').each((index: number, elem: HTMLElement) => {
			$(elem).attr('data-item', index);
		});
	}

	initVars() {
		this.$ctx = $(this._ctx);
		this.$arrowLeft = $(`.${classes.arrowLeft}`, this.$ctx);
		this.$arrowRight = $(`.${classes.arrowRight}`, this.$ctx);
		this.$scroller = $(`.${classes.scroller}`, this.$ctx);
		this.$items = $(`.${classes.item}`, this.$ctx);
		this.$imagesAndVideos = $('img, video', this.$ctx);
		this.$list = $(`.${classes.list}`, this.$ctx);
		this.$links = $(`.${classes.link}`, this.$ctx);
		this.$dotContainers = $(`.${classes.dot}`, this.$ctx);
		this.$dots = $(`.${classes.dotButton}`, this.$ctx);
		this.$sliderPages = $(`.${classes.sliderPages}`, this.$ctx);
		this.$sliderNavigation = $(`.${classes.sliderNavigation}`, this.$ctx);
		this.$campaignTeaserArea = $(`.${classes.campaignTeaserArea}`, this.$ctx);
		this.itemsPerPage = this.getItemsPerPageConfig();
		this.numberOfItemsPerPageBeforeViewportChanged = this.getNumberOfItemsPerPage();
		this.itemMargin = 16;
		if (this.$ctx.hasClass('m-slider-gallery--campaign')) {
			this.itemMargin = 20;
		}
		this.activeItemBeforeViewportChanged = 0;
		this.isMobileSlider = false;
		this.sliderIsScrolling = false;
		this.sliderPageIndex = 0;
		this._showDotsSlider = this.$sliderNavigation.data('show-number');
	}

	getItemsPerPageConfig() {
		const defaultItemsPerPage = [1, 1, 1, 1];
		const itemsPerPageData = this.$ctx.data('items-per-page');
		const itemsPerPage = defaultItemsPerPage;

		if (itemsPerPageData instanceof Array) {
			itemsPerPage.splice(0, itemsPerPageData.length, ...itemsPerPageData);
		} else if (Number.isInteger(itemsPerPageData)) {
			itemsPerPage.fill(itemsPerPageData);
		}

		return itemsPerPage;
	}

	handleVideoImageLoading() {
		// before being loaded completely the responsive image height is unknown so we wait for it to be loaded
		if (this.$imagesAndVideos.length > 0) {
			const mediaelements = [] as Array<Promise<any>>;
			this.$imagesAndVideos.slice(0, this.getNumberOfItemsPerPage()).each((i: number, el: HTMLElement) => {
				mediaelements.push(
					new Promise((resolve: any) => {
						const $el = $(el);
						$el.on('load.SliderGalleryImages error.SliderGalleryImages', () => {
							resolve();
						});
					})
				);
			});
			Promise.all(mediaelements).then(() => {
				this.sliderGalleryScroller.refresh();
				this.goToPageX(Math.floor(this.activeItemBeforeViewportChanged / this.getNumberOfItemsPerPage()));
				this.handleSizeAndPosition();

				// timeout to get initial dimensions without transition
				setTimeout(() => {
					this.$ctx.addClass(classes.isInitialized);
				}, 500);
			});
		} else {
			this.$ctx.addClass(classes.isInitialized);
		}
	}

	initNavigationArrows() {
		// bind event on the left arrow  to go to active page minus 1 (to left)
		this.$arrowLeft.on('click.SliderGalleryGotoLeft', (e: Event) => this.evtClickSkipBackOrForth(e, -1));

		// bind event on the right arrow to go to active page plus 1 (to right)
		this.$arrowRight.on('click.SliderGalleryGotoRight', (e: Event) => this.evtClickSkipBackOrForth(e, 1));
	}

	handleSizeAndPosition() {
		this.setFixedPageWidthAndArrowPosition();
		this.setScrollerHeightAccordingToPageHeight();
	}

	handleCampaignTeaserClick() {
		this.$campaignTeaserArea.on('click', this.openModalAndCopyText.bind(this));
	}

	openModalAndCopyText(e: any) {
		const viewport = getCurrentBreakpoint() as string;
		const theme = this.$ctx.data('theme');

		if (viewport === 'small') {
			$(e.currentTarget)
				.clone()
				.appendTo($('.m-modal-overlay'));
			$('html').addClass('no-scrolling--without-scrollbar');
			$('.m-modal-overlay').addClass(theme);
			$(window).trigger('modalWindow:open');
		}
	}

	/* 	we set the page width fixed in px cause % is not working in horizontal iScroll
		on top there is some arrow positioning magic, centered to the height of the
		content of a page or it's image/video part if available
	 */
	setFixedPageWidthAndArrowPosition() {
		const numberOfItems = this.getNumberOfItemsPerPage();
		const totalMarginWidth = this.itemMargin * (numberOfItems - 1);
		const itemWidth = (this.$scroller.outerWidth() - totalMarginWidth) / numberOfItems;

		this.$items.css('width', `${itemWidth}px`);
		this.sliderGalleryScroller.refresh();

		const biggestItem = this.getBiggestItemAndHeightOfPage(0).item;
		const $imgVideoEl = $('img, video', biggestItem);
		const isFullPageSizeMedium = Math.floor($imgVideoEl.outerWidth()) >= Math.floor(itemWidth);
	}

	setScrollerHeightAccordingToPageHeight() {
		const biggestItemHeight = this.getBiggestItemAndHeightOfPage(this.sliderGalleryScroller.currentPage.pageX)
			.height;
		this.$list.css('height', `${biggestItemHeight}px`);
	}

	getBiggestItemAndHeightOfPage(page: number) {
		const itemsPerPage = Math.min(this.$items.length, this.getNumberOfItemsPerPage());
		const itemToStartWith = page * itemsPerPage;

		let biggestItemHeight = 0;
		let biggestItem = this.$items.eq(itemToStartWith);

		for (let i = itemToStartWith; i < itemToStartWith + itemsPerPage; i++) {
			if (biggestItemHeight < this.$items[i].offsetHeight) {
				biggestItemHeight = this.$items[i].offsetHeight;
				biggestItem = this.$items.eq(i);
			}
		}

		return {
			height: biggestItemHeight,
			item: biggestItem,
		};
	}

	setArrowPosition(arrowTopPosition: number) {
		this.$arrowLeft.css('top', `${arrowTopPosition}px`);
		this.$arrowRight.css('top', `${arrowTopPosition}px`);
	}

	setActiveDot(pageNumber: number) {
		if (!this.isMobileSlider) {
			this.$dots
				.removeClass(classes.activeDot)
				.eq(pageNumber)
				.addClass(classes.activeDot);
		}

		this.$sliderPages.text(formatLabel(this.$sliderNavigation.data('pages'), pageNumber + 1, this.$items.length))

		this.$arrowLeft.toggleClass(classes.showArrow, this.sliderGalleryScroller.currentPage.pageX > 0);
		this.$arrowRight.toggleClass(
			classes.showArrow,
			this.sliderGalleryScroller.currentPage.pageX < this.sliderGalleryScroller.pages.length - 1
		);

		this.setScrollerHeightAccordingToPageHeight();
		this.activeItemBeforeViewportChanged = pageNumber * this.getNumberOfItemsPerPage();
		this.sliderPageIndex = pageNumber;
	}

	reinitScrollerWhenViewportChangedToOrFromSmall() {
		const numberOfItemsPerPage = this.getNumberOfItemsPerPage();

		if (numberOfItemsPerPage !== this.numberOfItemsPerPageBeforeViewportChanged) {
			this.initScroller();
			this.numberOfItemsPerPageBeforeViewportChanged = numberOfItemsPerPage;
		}
	}

	// the following function holds all logic for having different amount of items on viewports/cases. default is 1
	getNumberOfItemsPerPage() {
		const viewport = getCurrentBreakpoint() as string;

		switch (viewport) {
			case 'extra-large':
				return this.itemsPerPage[3];
			case 'large':
				return this.itemsPerPage[2];
			case 'medium':
				return this.itemsPerPage[1];
			default:
				return this.itemsPerPage[0];
		}
	}

	// inits scroller and it's pages
	initScroller() {
		const numberOfItemsPerPage = this.getNumberOfItemsPerPage();

		this.initPageDots(numberOfItemsPerPage);

		this.newIscroll(numberOfItemsPerPage);

		this.initScrollerEvents();

		this.initItemLinkBehaviour();

		// set initial active dot
		const startSlide = Math.floor(this.activeItemBeforeViewportChanged / numberOfItemsPerPage);
		this.goToPageX(startSlide);
		this.trackSlide(startSlide);
	}

	initPageDots(numberOfItemsPerPage: number) {
		this.showOrHideDots(numberOfItemsPerPage);

		// bind event on the dots to click through all pages
		this.$dots.each((index: number, elem: HTMLElement) => {
			$(elem).on('click.SliderGalleryDotGotoPage', (e: Event) => this.evtClickDot(e, index));
			$(elem).attr('data-item', index);
		});
	}

	showOrHideDots(numberOfItemsPerPage: number) {
		this.$dotContainers.show();

		if (numberOfItemsPerPage > 1) {
			this.$dotContainers.not(`:nth-child(${numberOfItemsPerPage}n + 1)`).hide();

			const visibleDotContainers = $(`.${classes.dot}:nth-child(${numberOfItemsPerPage}n + 1)`, this.$ctx);
			this.$dots = $(`.${classes.dotButton}`, visibleDotContainers);
		} else {
			this.$dots = $(`.${classes.dotButton}`, this.$ctx);
		}

		// dont show dots if only one page/dot available
		if (this.$dots.length > 1) {
			this.$sliderNavigation.css('display', 'flex');
		} else {
			this.$sliderNavigation.hide();
		}
	}

	newIscroll(numberOfItemsPerPage: number) {
		const viewport = getCurrentBreakpoint() as string;
		if (viewport === 'small') {
			this.sliderGalleryScroller = new IScroll($(`.${classes.scroller}`, this.$ctx)[0], {
				snap: true,
				momentum: false,
				scrollX: false, // Enable horizontal scrolling
				scrollY: false,
			});
			this.initMobileSlider();
			this.isMobileSlider = true;
		} else {
			this.sliderGalleryScroller = new IScroll($(`.${classes.scroller}`, this.$ctx)[0], {
				mouseWheel: false,
				scrollbars: false,
				scrollX: true,
				scrollY: false,
				snap: numberOfItemsPerPage > 1 ? `li:nth-child(${numberOfItemsPerPage}n + 1)` : 'li',
				tap: true,
			});
		}
	}

	initMobileSlider() {
		const $elements = this.$ctx.find('li.js-m-slider-gallery__item');
		const dotItems = this.$ctx.find('.js-m-slider-gallery__dot-button');
		let slideItemIndex = 0;
		let currentIndex = 0;
		let startX: any;
		let startY: any;
		let sdeltaX: any;
		let sdeltaY: any;
		let isMove = false;
		let indexItem: number;

		$elements.each((index: number, elem: HTMLElement) => {
			$(elem).on('touchstart', (e: any) => {
				e.stopImmediatePropagation();
				startX = e.originalEvent.touches[0].clientX;
				startY = e.originalEvent.touches[0].clientY;
				isMove = false;
			});
			$(elem).on('touchmove', (e: any) => {
				e.preventDefault();
				isMove = true;
			});
			$(elem).on('touchend', (e: any) => {
				let newItemIndex: number = 0;
				const items = this.$items.length;
				let getSlideIndex = parseInt(e.currentTarget.dataset.item);

				sdeltaX = Math.ceil(e.originalEvent.changedTouches[0].clientX - startX);
				sdeltaY = Math.ceil(e.originalEvent.changedTouches[0].clientY - startY);
				newItemIndex = sdeltaX < 0 ? index + 1 : indexItem === 0 ? items - 1 : indexItem - 1;

				if (isMove) {
					let endX = e.originalEvent.changedTouches[0].clientX;
					let endY = e.originalEvent.changedTouches[0].clientY;

					let deltaX = endX - startX;
					let deltaY = endY - startY;

					if (Math.abs(deltaX) > Math.abs(deltaY)) {
						// Horizontal swipe
						if (deltaX > 0) {
							// Swipe right
							currentIndex = (getSlideIndex - 1 + items) % items;
							this.setActiveSlide(currentIndex);
						} else {
							// Swipe left
							currentIndex = (getSlideIndex + 1) % items;
							this.setActiveSlide(currentIndex);
						}
					}
				}
			});
		});

		// bind event on the dots to click through all pages
		this.$dots.each((index: number, elem: HTMLElement) => {
			$(elem).on('click.SliderGalleryDotGotoSlide', (e: Event) => this.setActiveSlide(index));
		});
		//this.$dotContainers.hide();

		this.setActiveSlide(slideItemIndex);
	}

	// Set active Slide item
	setActiveSlide(el: any) {
		this.$items
			.addClass('inactive')
			.removeClass('active')
			.eq(el)
			.removeClass('inactive')
			.addClass('active');
		this.$dots
			.removeClass(classes.activeDot)
			.eq(el)
			.addClass(classes.activeDot);
	}

	//this.$dotContainers.hide();



	initScrollerEvents() {
		this.sliderGalleryScroller.on('scrollStart', () => {
			this.sliderIsScrolling = true;
		});

		this.sliderGalleryScroller.on('scrollEnd', () => {
			const activePage = this.sliderGalleryScroller.currentPage.pageX;
			this.setActiveDot(activePage);
			this.sliderIsScrolling = false;
			this.trackSlide(activePage);
		});
	}

	initItemLinkBehaviour() {
		// bind event on the links to prevent default while scrolling
		this.$links.on('click.SliderGalleryClickLink', (ev: Event) => {
			if (this.sliderIsScrolling) {
				ev.stopPropagation();
				ev.preventDefault();
			}
		});
	}

	goToPageX(pageNumber: number) {
		this.sliderGalleryScroller.goToPage(pageNumber, 0, 1000);
		this.setActiveDot(pageNumber);
		this.sliderPageIndex = pageNumber;
	}

	/**
	 * Click Event for Dots in Slider-Gallery
	 * @param {event} e event
	 * @param {number} index index
	 * @returns {void} return nothing
	 */
	evtClickDot(e: any, index: number) {
		const currentPage = this.sliderGalleryScroller.currentPage.pageX;
		// eslint-disable-next-line newline-per-chained-call
		const $target = $(e.currentTarget)
			.parent()
			.parent()
			.prev();
		const $videosContainer = $target.parent().find(`.${classes.videosContainer}`);

		this.stopVideoInCurrentSlider($videosContainer, index, currentPage);
		this.goToPageX(index);
	}

	/**
	 * Click Event for Skip Back/Next slider in Slider-Gallery
	 * @param {event} e event
	 * @param {number} sliderIdxOffset Positive integer value will increase and negative will decrease the slider page
	 * @returns {void} return nothing
	 */
	evtClickSkipBackOrForth(e: Event, sliderIdxOffset: number) {
		const nextPage = this.sliderGalleryScroller.currentPage.pageX + sliderIdxOffset;
		const $target = $(e.currentTarget)
			.parent()
			.find(`.${classes.videosContainer}`);

		this.stopVideoInCurrentSlider($target, nextPage, this.sliderGalleryScroller.currentPage.pageX);
		this.goToPageX(nextPage);
		$(window).trigger('NavigationInPage:reInitNoClick');
	}

	/**
	 * Stop Video In Current Slider
	 * @param {Object} $videosContainer video container
	 * @param {number} currentPage current page index
	 * @param {number} prevSlide previous page index
	 * @returns {void} return nothing
	 */
	stopVideoInCurrentSlider($videosContainer: any, currentPage: number, prevSlide: number) {
		const $listItems: any = $videosContainer.find(`.${classes.videosContainerListItem}`);
		const $videoElement: any = $($listItems[prevSlide]).find(`.${classes.videoElement}`);
		const videoElementId: any = $videoElement.attr(videoCfg.targetId);
		// eslint-disable-next-line prefer-template
		const newVideoAttribute: string = videoElementId + '__active';

		// If undefined otherwise will throw an error, when there was image instead a video in  slide where to stop a video
		const hasAttr: boolean = $videoElement.attr(videoCfg.targetId) !== undefined;

		if (hasAttr) {
			$videoElement.attr('id', newVideoAttribute);
			this.callVideoAPIMethod($videoElement.attr('id'));
			$videoElement.removeAttr('id');
		}
	}

	/**
	 * Call Video API Methods
	 *
	 * 		Possible values for Transport Controls:
	 *
	 *  	play
	 *  	pause
	 *  	stop
	 *  	forward
	 *  	rewind
	 *  	setVolume
	 *  	toggleMute
	 *  	getCurrentTime
	 *  	getDuration
	 *  	getBufferedTime
	 *  	switchVideo
	 *
	 * 		Actually makes window.VideoPlayer.Collection.getPlayerById('someId').pause(); or something else you define
	 * 		Don't forget the dependency to the embed script of movingimages.com from http//e.video-cdn.net/v2/embed.js
	 * 		Otherwise <window.VideoPlayer.Collection.*> will not be reachable in the code and JS errors will occur
	 * 		or the videos will not be loaded.
	 *
	 * @param {string} id id container for video player to be stopped or paused
	 * @returns {void} return nothing
	 */
	callVideoAPIMethod(id: string) {
		// eslint-disable-next-line dot-notation
		(window as any)['VideoPlayer'].Collection.getPlayerById(id)[videoCfg.typeOnStopWhenSkippingSlide]();
	}

	trackSlide(pageNumber: number) {
		if (this.$items[pageNumber]) {
			const activeItem = this.$items[pageNumber];
			const title = activeItem.getAttribute('data-title');
			this.track(title, pageNumber);
		}
	}

	track(name: string, position: number) {
		const dataLayer = (window as any).dataLayer || [];
		let list = '';
		if (dataLayer) {
			list = dataLayer[0].campaignName || '';
		}
		const trackdata: {
			event: string;
			ecommerce: {
				promoView: {
					promotions: Array<{
						name: string;
						position: number;
						list: string;
					}>;
				};
			};
		} = {
			event: 'sliderImpression',
			ecommerce: {
				promoView: {
					promotions: [
						{
							name: name,
							position: position,
							list: list,
						},
					],
				},
			},
		};
		Track.gtmTrack(trackdata);
	}

	/**
	 * Slider Page Index
	 * @returns {void} return nothing
	 */
	get sliderPageIndex(): number {
		return this._sliderPageIndex;
	}

	/**
	 * Slider Page Index
	 * @param {number} value index
	 * @returns {void} return nothing
	 */
	set sliderPageIndex(value: number) {
		this._sliderPageIndex = value;
	}

	stop() { }
}
